import React, {useEffect, useMemo, useRef, useState} from "react";
import {OrderEditorTabProps} from "@widgets/pos/order/OrderEditor";
import CardHeader from "peggirkit/dist/components/Card/CardHeading/CardHeader";
import {Alert, Card, InputLabel, Select, SelectOptionProps, SimpleCardHeading} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {Controller, useFormContext} from "react-hook-form";
import dayjs from "dayjs";
import {PosOrderForm} from "@/data/entities/order";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import {ASAP, requestedTimeSlots, toRequestedTime} from "./RequestedTimeSlots";

const REFRESH_SLOTS_MS = 60_000;

/**
 * Requested delivery or pickup time: as soon as possible, or a time slot
 * today within the opening hours.
 */
const RequestedTimeCard = ({isPending}: OrderEditorTabProps) => {
    const intl = useIntl();
    const {control, getValues, setValue} = useFormContext<PosOrderForm>();
    const {dashboardLocation} = useRestaurant();
    const fulfillmentType = useFulfillmentType();

    // Slots in the past disappear while the editor is open
    const [now, setNow] = useState(dayjs());
    useEffect(() => {
        const interval = setInterval(() => setNow(dayjs()), REFRESH_SLOTS_MS);
        return () => clearInterval(interval);
    }, []);

    const slots: string[] = useMemo(
        () => requestedTimeSlots(dashboardLocation.openingWeek, fulfillmentType, now).map(toRequestedTime),
        [dashboardLocation.openingWeek, fulfillmentType, now],
    );

    // Delivery and takeaway hours differ: a chosen time may no longer be valid
    const previousFulfillmentType = useRef(fulfillmentType);
    useEffect(() => {
        if (previousFulfillmentType.current === fulfillmentType) return;
        previousFulfillmentType.current = fulfillmentType;

        const requestedTime = getValues("requestedTime");
        if (requestedTime !== ASAP && !slots.includes(requestedTime))
            setValue("requestedTime", ASAP, {shouldDirty: true});
    }, [fulfillmentType, slots, getValues, setValue]);

    const toOption = (value: string): SelectOptionProps<undefined> => ({
        value,
        displayValue: value === ASAP
            ? intl.formatMessage({id: "pos.order.asap"})
            : dayjs(value).isSame(now, "day")
                ? dayjs(value).format("HH:mm")
                : dayjs(value).format("DD-MM HH:mm"),
    });

    return (
        <Card
            header={
                <CardHeader>
                    <SimpleCardHeading
                        title={<FormattedMessage id={fulfillmentType === "DELIVERY"
                            ? "requestedDeliveryTime"
                            : "requestedPickupTime"}/>}
                    />
                </CardHeader>
            }
        >
            <div className={"flex flex-col gap-4"}>
                <Controller
                    control={control}
                    name={"requestedTime"}
                    rules={{required: intl.formatMessage({id: "pick-required-option"})}}
                    render={({field, fieldState}) => {
                        const values: string[] = [ASAP, ...slots];
                        // Keep a time that is no longer offered (e.g. an existing order) selectable
                        if (field.value && !values.includes(field.value))
                            values.push(field.value);
                        const options = values.map(toOption);

                        return (
                            <Select<undefined>
                                label={
                                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                                        <FormattedMessage id={"requested-time"}/>
                                    </InputLabel>
                                }
                                displayValueEmpty={intl.formatMessage({id: "pos.order.asap"})}
                                options={options}
                                value={options.find(option => option.value === field.value) ?? null}
                                onChange={option => field.onChange(`${option.value}`)}
                                disabled={isPending}
                                error={fieldState.error?.message}
                            />
                        );
                    }}
                />

                {
                    slots.length === 0 &&
                    <Alert
                        type={"warning"}
                        title={<FormattedMessage id={"noTimeSlotsLeft"}/>}
                        content={<FormattedMessage id={"noTimeSlotsLeftDescription"}/>}
                    />
                }
            </div>
        </Card>
    );
};

export default RequestedTimeCard;